import {
  Bindings,
  ConditionalExpression,
} from '@jetfabric/schemas/lib/protocol-sdk-api-v2/query/condition';
import {
  DataReference,
  Literal,
  Value,
} from '@jetfabric/schemas/lib/protocol-sdk-api-v2/query/data-reference';
import { V2Query } from '@jetfabric/schemas/lib/protocol-sdk-api-v2/query/queries';
import { TraceState } from '@jetfabric/schemas/lib/protocol-sdk-api-v2/query/trace';
import { JSONPath } from 'jsonpath-plus';
import { Either, isLeft, left, right } from './either';
import { isDataReference } from './guards';

/**
 PartialEvaluation is the *Promise* of *Either*:

 - *Left*  - the unevaluated remainder of the ConditionalExpression
 - *Right* - full evaluation (Literal)

*/
export type PartialEvaluation = Promise<Either<ConditionalExpression, Literal>>;

export type CachedBindings = {
  cache: { [key: string]: PartialEvaluation };
  bindings: Bindings;
};

// A DataReferenceResolver returns the Promise of Either:
//    Right - a resolved value (Literal) or
//    Left  - the unevaluated DataReferenceResolver.
// Theoretically, the returned reference can be a different one than the original ref.
// An exception may also occur if resolution fails.
export type DataReferenceResolver = (
  ref: DataReference,
  bindingsStack: CachedBindings[],
  traceState?: TraceState
) => Promise<Either<DataReference, Value>>;

export type QueryResolver = (
  query: V2Query,
  bindingsStack: CachedBindings[],
  traceState?: TraceState
) => Promise<Either<V2Query, any>>;

export const isVoid = (value: any) => value === undefined || value === null;

export const unpackDataReference = async (
  ref: DataReference,
  data: any,
  resolve: DataReferenceResolver,
  bindingsStack: CachedBindings[],
  traceState?: TraceState
): Promise<Either<DataReference, Value>> => {
  let json = data;
  if (ref.path && data !== undefined && data !== null) {
    [json] = JSONPath({ path: ref.path, json });
  }
  if (isVoid(json) && ref.fallback !== undefined) {
    return isDataReference(ref.fallback)
      ? await resolve(ref.fallback, bindingsStack, traceState)
      : right(ref.fallback);
  }
  return right(json);
};

export const defaultDataReferenceResolver: (q: QueryResolver) => DataReferenceResolver = (
  queryResolver: QueryResolver
) => {
  const resolver = async (
    ref: DataReference,
    bindingsStack: CachedBindings[],
    traceState?: TraceState
  ) => {
    const maybeResult = await queryResolver(ref.query, bindingsStack, traceState);
    if (isLeft(maybeResult)) {
      // query was only partially resolved, return data reference w/ partially resolved query:
      return left({ ...ref, query: maybeResult.value });
    }
    return await unpackDataReference(ref, maybeResult.value, resolver, bindingsStack, traceState);
  };
  return resolver;
};
